import type { Contract, ContractStage, SmoUlClosingStatus, Task } from '../services/api';
import type { TaskDeadlineGanttRow } from '../components/TaskDeadlinesGantt';
import { formatDateShort } from './table';
import { formatDeadlineRelativeTo } from './deadline';
import { maxFamilyDate, minFamilyDate } from './contractFamily';

export type DeadlineStatusVariant = 'ok' | 'warn' | 'neutral' | 'muted';

export interface ContractDeadlineTableRow {
  key: string;
  contractId?: number;
  contractNumber: string;
  taskId: string;
  workType: string;
  startDate: string | null;
  endDate: string | null;
  isTotal: boolean;
  isContractRow: boolean;
  isTaskRow: boolean;
  isCurrent: boolean;
  showContractNumber: boolean;
  contractRowSpan: number;
  showTaskId: boolean;
  taskRowSpan: number;
  showWorkType: boolean;
  workTypeRowSpan: number;
  expenseClosingDisplay: string;
  statusLabel: string;
  statusVariant: DeadlineStatusVariant;
  deltaDisplay: string;
  deltaVariant: DeadlineStatusVariant;
}

export interface ContractDeadlinesSummary {
  contractCount: number;
  taskCount: number;
  overdueTaskCount: number;
  closedOnTimeCount: number;
  allExpensesClosed: boolean;
}

export interface ContractDeadlinesData {
  tableRows: ContractDeadlineTableRow[];
  ganttRows: TaskDeadlineGanttRow[];
  spanStartMs: number;
  spanEndMs: number;
  spanContentEndMs: number;
  axisStartLabel: string;
  axisEndLabel: string;
  totalOverdue: boolean | null;
  todayMs: number;
  referenceMarkerMs: number;
  referenceMarkerLabel: string;
  familyExpenseClosingDisplay: string;
  summary: ContractDeadlinesSummary;
}

interface RowSpanCell {
  show: boolean;
  rowSpan: number;
}

function mergeConsecutiveRowSpanCells<T>(rows: T[], valueFn: (row: T) => string): RowSpanCell[] {
  const cells: RowSpanCell[] = rows.map(() => ({ show: false, rowSpan: 1 }));
  let index = 0;
  while (index < rows.length) {
    let end = index + 1;
    const value = valueFn(rows[index]);
    while (end < rows.length && valueFn(rows[end]) === value) {
      end += 1;
    }
    cells[index] = { show: true, rowSpan: end - index };
    index = end;
  }
  return cells;
}

function parseTaskDay(value?: string | null): number | null {
  if (!value?.trim()) return null;
  const ms = new Date(`${value.trim()}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function todayStartMs(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function maxDate(values: Array<string | null | undefined>): string | null {
  let max: string | null = null;
  for (const raw of values) {
    const v = raw?.trim();
    if (!v) continue;
    if (max == null || v > max) max = v;
  }
  return max;
}

/** Закрытие расходов в срок: дата закрытия есть и не позже крайнего срока. */
export function resolveExpenseClosedOnTime(
  endDate: string | null | undefined,
  expenseClosingDate: string | null | undefined,
): boolean {
  if (!expenseClosingDate?.trim()) return false;
  const end = endDate?.trim();
  if (!end) return true;
  return expenseClosingDate.trim() <= end;
}

function daysBetweenDates(later: string, earlier: string): number {
  const a = new Date(`${earlier.trim()}T00:00:00`).getTime();
  const b = new Date(`${later.trim()}T00:00:00`).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

function formatOverdueDaysLabel(endDate: string, referenceDate: string): string {
  const rel = formatDeadlineRelativeTo(endDate, referenceDate);
  if (!rel.overdue) return 'в срок';
  const totalDays = rel.months != null && rel.days != null ? rel.months * 30 + rel.days : 0;
  return totalDays > 0 ? `+${totalDays} дн.` : 'просрочка';
}

function resolveDeadlineStatus(
  endDate: string | null | undefined,
  referenceDate: string,
): { label: string; variant: DeadlineStatusVariant } {
  if (!endDate?.trim()) {
    return { label: '—', variant: 'muted' };
  }
  const rel = formatDeadlineRelativeTo(endDate, referenceDate);
  if (rel.overdue) {
    const totalDays = rel.months != null && rel.days != null ? rel.months * 30 + rel.days : 0;
    return { label: totalDays > 0 ? `+${totalDays} дн.` : 'просрочка', variant: 'warn' };
  }
  if (rel.label === 'Осталось') {
    return { label: 'в срок', variant: 'ok' };
  }
  return { label: '—', variant: 'neutral' };
}

function resolveTaskDelta(
  endDate: string | null | undefined,
  expenseClosingDate: string | null | undefined,
  referenceDate: string,
): { text: string; variant: DeadlineStatusVariant } {
  const closing = expenseClosingDate?.trim();
  const end = endDate?.trim();

  if (closing) {
    if (resolveExpenseClosedOnTime(endDate, expenseClosingDate)) {
      return { text: 'в срок', variant: 'ok' };
    }
    if (end) {
      const lateDays = daysBetweenDates(closing, end);
      return { text: lateDays > 0 ? `+${lateDays} дн.` : 'с опозданием', variant: 'warn' };
    }
    return { text: 'с опозданием', variant: 'warn' };
  }

  if (!end) {
    return { text: '—', variant: 'muted' };
  }

  const rel = formatDeadlineRelativeTo(end, referenceDate);
  if (rel.overdue) {
    return { text: formatOverdueDaysLabel(end, referenceDate), variant: 'warn' };
  }
  return { text: 'не закрыто', variant: 'neutral' };
}

function resolveContractMaxClosing(tasks: Task[]): string | null {
  return maxDate(tasks.map((task) => task.expense_closing_date));
}

function resolveContractDelta(
  contractEnd: string | null | undefined,
  maxClosingDate: string | null,
  referenceDate: string,
): { text: string; variant: DeadlineStatusVariant } {
  if (maxClosingDate?.trim()) {
    return resolveTaskDelta(contractEnd, maxClosingDate, referenceDate);
  }
  if (!contractEnd?.trim()) {
    return { text: '—', variant: 'muted' };
  }
  return resolveDeadlineStatus(contractEnd, referenceDate);
}

function resolveBarOverdue(
  planEnd: string | null | undefined,
  factClosing: string | null,
  referenceDate: string,
): boolean {
  if (factClosing?.trim()) {
    if (!planEnd?.trim()) return false;
    return !resolveExpenseClosedOnTime(planEnd, factClosing);
  }
  if (!planEnd?.trim()) return false;
  return formatDeadlineRelativeTo(planEnd, referenceDate).overdue;
}

function emptyRowStatus(): Pick<
  ContractDeadlineTableRow,
  'statusLabel' | 'statusVariant' | 'deltaDisplay' | 'deltaVariant'
> {
  return {
    statusLabel: '—',
    statusVariant: 'muted',
    deltaDisplay: '—',
    deltaVariant: 'muted',
  };
}

function sortContractTasks(tasks: Task[], workTypeLabel: (id: number) => string): Task[] {
  return [...tasks].sort((left, right) => {
    const byTaskId = left.task_id.localeCompare(right.task_id, 'ru');
    if (byTaskId !== 0) return byTaskId;
    return workTypeLabel(left.work_type_id).localeCompare(workTypeLabel(right.work_type_id), 'ru');
  });
}

const SPAN_END_PADDING_MONTHS = 2;

function addCalendarMonths(ms: number, months: number): number {
  const d = new Date(ms);
  d.setMonth(d.getMonth() + months);
  return d.getTime();
}

function countUniqueTaskSummary(
  familyTasks: Task[],
  expenseReferenceDate: string,
): Pick<ContractDeadlinesSummary, 'taskCount' | 'overdueTaskCount' | 'closedOnTimeCount'> {
  const byTaskId = new Map<string, Task[]>();
  for (const task of familyTasks) {
    const list = byTaskId.get(task.task_id) ?? [];
    list.push(task);
    byTaskId.set(task.task_id, list);
  }

  let overdueTaskCount = 0;
  let closedOnTimeCount = 0;

  for (const tasks of byTaskId.values()) {
    let anyOverdue = false;
    let allClosedOnTime = true;
    let anyClosed = false;

    for (const task of tasks) {
      const closedOnTime = resolveExpenseClosedOnTime(task.end_date, task.expense_closing_date);
      const delta = resolveTaskDelta(task.end_date, task.expense_closing_date, expenseReferenceDate);
      if (delta.variant === 'warn') anyOverdue = true;
      if (!closedOnTime) allClosedOnTime = false;
      if (task.expense_closing_date?.trim()) anyClosed = true;
    }

    if (anyOverdue) overdueTaskCount += 1;
    if (allClosedOnTime && anyClosed) closedOnTimeCount += 1;
  }

  return {
    taskCount: byTaskId.size,
    overdueTaskCount,
    closedOnTimeCount,
  };
}

export interface FamilyExpenseClosingSummary {
  allClosed: boolean;
  maxClosingDate: string | null;
  maxClosingMs: number | null;
}

export function resolveFamilyExpenseClosingSummary(familyTasks: Task[]): FamilyExpenseClosingSummary {
  const uniqueTaskIds = new Set(familyTasks.map((t) => t.task_id));
  const sampleByTaskId = new Map<string, Task>();
  for (const task of familyTasks) {
    if (!sampleByTaskId.has(task.task_id)) {
      sampleByTaskId.set(task.task_id, task);
    }
  }

  let allClosed = uniqueTaskIds.size > 0;
  let maxClosingMs: number | null = null;

  for (const taskId of uniqueTaskIds) {
    const task = sampleByTaskId.get(taskId);
    const closing = task?.expense_closing_date;
    if (!closing?.trim()) {
      allClosed = false;
      continue;
    }
    const ms = parseTaskDay(closing);
    if (ms == null) {
      allClosed = false;
      continue;
    }
    maxClosingMs = maxClosingMs == null ? ms : Math.max(maxClosingMs, ms);
  }

  return {
    allClosed,
    maxClosingMs: allClosed ? maxClosingMs : null,
    maxClosingDate: allClosed && maxClosingMs != null ? msToDateString(maxClosingMs) : null,
  };
}

export function resolveFamilyExpenseClosingCheck(familyTasks: Task[]): {
  status: SmoUlClosingStatus;
  closingDate: string | null;
} {
  const summary = resolveFamilyExpenseClosingSummary(familyTasks);
  if (summary.allClosed) {
    return { status: 'closed', closingDate: summary.maxClosingDate };
  }

  const sampleByTaskId = new Map<string, Task>();
  for (const task of familyTasks) {
    if (!sampleByTaskId.has(task.task_id)) {
      sampleByTaskId.set(task.task_id, task);
    }
  }
  if (sampleByTaskId.size === 0) {
    return { status: 'open', closingDate: null };
  }

  let closedCount = 0;
  for (const task of sampleByTaskId.values()) {
    if (task.expense_closing_date?.trim()) {
      closedCount += 1;
    }
  }

  return {
    status: closedCount > 0 ? 'partial' : 'open',
    closingDate: null,
  };
}

function resolveFamilyTotalMarkerMs(familyTasks: Task[], todayMs: number): number {
  const { allClosed, maxClosingMs } = resolveFamilyExpenseClosingSummary(familyTasks);
  if (allClosed && maxClosingMs != null) {
    return maxClosingMs;
  }
  return todayMs;
}

function msToDateString(ms: number): string {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatMsAsDateShort(ms: number): string {
  return formatDateShort(msToDateString(ms));
}

/** Опорная дата для просрочки: сегодня или макс. дата закрытия расходов по семье задач. */
export function resolveFamilyExpenseReferenceDate(familyTasks: Task[]): string {
  const todayMs = todayStartMs();
  return msToDateString(resolveFamilyTotalMarkerMs(familyTasks, todayMs));
}

export function resolveFamilyStageClosingSummary(stages: ContractStage[]): FamilyExpenseClosingSummary {
  if (stages.length === 0) {
    return { allClosed: false, maxClosingMs: null, maxClosingDate: null };
  }

  let allClosed = true;
  let maxClosingMs: number | null = null;

  for (const stage of stages) {
    const closing = stage.accounting_closing_date;
    if (!stage.accounting_closed || !closing?.trim()) {
      allClosed = false;
      continue;
    }
    const ms = parseTaskDay(closing);
    if (ms == null) {
      allClosed = false;
      continue;
    }
    maxClosingMs = maxClosingMs == null ? ms : Math.max(maxClosingMs, ms);
  }

  return {
    allClosed,
    maxClosingMs: allClosed ? maxClosingMs : null,
    maxClosingDate: allClosed && maxClosingMs != null ? msToDateString(maxClosingMs) : null,
  };
}

export function resolveFamilyStageClosingCheck(stages: ContractStage[]): {
  status: SmoUlClosingStatus;
  closingDate: string | null;
} {
  const summary = resolveFamilyStageClosingSummary(stages);
  if (summary.allClosed) {
    return { status: 'closed', closingDate: summary.maxClosingDate };
  }
  if (stages.length === 0) {
    return { status: 'open', closingDate: null };
  }
  const closedCount = stages.filter((stage) => Boolean(stage.accounting_closed)).length;
  return {
    status: closedCount > 0 ? 'partial' : 'open',
    closingDate: null,
  };
}

export function resolveFamilyStageReferenceDate(stages: ContractStage[]): string {
  const todayMs = todayStartMs();
  const { allClosed, maxClosingMs } = resolveFamilyStageClosingSummary(stages);
  if (allClosed && maxClosingMs != null) {
    return msToDateString(maxClosingMs);
  }
  return msToDateString(todayMs);
}

function stageDeadlineLabel(stage: ContractStage): string {
  return `${stage.stage_number}. ${stage.name}`.trim();
}

function sortContractStages(stages: ContractStage[]): ContractStage[] {
  return [...stages].sort((left, right) => {
    if (left.stage_number !== right.stage_number) return left.stage_number - right.stage_number;
    return left.id - right.id;
  });
}

function countStageSummary(
  stages: ContractStage[],
  expenseReferenceDate: string,
): Pick<ContractDeadlinesSummary, 'taskCount' | 'overdueTaskCount' | 'closedOnTimeCount'> {
  let overdueTaskCount = 0;
  let closedOnTimeCount = 0;

  for (const stage of stages) {
    const closedOnTime = resolveExpenseClosedOnTime(stage.planned_date_end, stage.accounting_closing_date);
    const delta = resolveTaskDelta(stage.planned_date_end, stage.accounting_closing_date, expenseReferenceDate);
    if (delta.variant === 'warn') overdueTaskCount += 1;
    if (closedOnTime && stage.accounting_closing_date?.trim()) closedOnTimeCount += 1;
  }

  return {
    taskCount: stages.length,
    overdueTaskCount,
    closedOnTimeCount,
  };
}

function resolveContractMaxStageClosing(stages: ContractStage[]): string | null {
  const summary = resolveFamilyStageClosingSummary(stages);
  return summary.allClosed ? summary.maxClosingDate : null;
}

function collectMarkedDatesMs(
  ganttRows: TaskDeadlineGanttRow[],
  totalMarkerMs: number | null,
): number[] {
  const marked: number[] = [];
  for (const row of ganttRows) {
    if (row.startMs != null) marked.push(row.startMs);
    if (row.endMs != null) marked.push(row.endMs);
    if (row.expenseClosingDateMs != null) marked.push(row.expenseClosingDateMs);
    if (row.isTotal && row.totalMarkerMs != null) marked.push(row.totalMarkerMs);
  }
  if (totalMarkerMs != null) marked.push(totalMarkerMs);
  return marked;
}

export function buildContractDeadlinesData(params: {
  familyContracts: Contract[];
  familyTasks: Task[];
  currentContractId?: number;
  workTypeLabel: (id: number) => string;
}): ContractDeadlinesData {
  const { familyContracts, familyTasks, currentContractId, workTypeLabel } = params;
  const empty: ContractDeadlinesData = {
    tableRows: [],
    ganttRows: [],
    spanStartMs: 0,
    spanEndMs: 0,
    spanContentEndMs: 0,
    axisStartLabel: '—',
    axisEndLabel: '—',
    totalOverdue: null,
    todayMs: todayStartMs(),
    referenceMarkerMs: todayStartMs(),
    referenceMarkerLabel: 'Сегодня',
    familyExpenseClosingDisplay: '—',
    summary: {
      contractCount: 0,
      taskCount: 0,
      overdueTaskCount: 0,
      closedOnTimeCount: 0,
      allExpensesClosed: false,
    },
  };

  if (familyContracts.length === 0) {
    return empty;
  }

  const todayMs = todayStartMs();
  const expenseSummary = resolveFamilyExpenseClosingSummary(familyTasks);
  const totalMarkerMs = resolveFamilyTotalMarkerMs(familyTasks, todayMs);
  const expenseReferenceDate = msToDateString(totalMarkerMs);
  const referenceMarkerLabel =
    expenseSummary.allClosed && expenseSummary.maxClosingMs != null
      ? 'Закрытие договора по расходам'
      : 'Сегодня';

  const tableRows: ContractDeadlineTableRow[] = [];
  const ganttRows: TaskDeadlineGanttRow[] = [];
  const uniqueTaskSummary = countUniqueTaskSummary(familyTasks, expenseReferenceDate);

  for (const contract of familyContracts) {
    const contractTasks = sortContractTasks(
      familyTasks.filter((t) => t.contract_id === contract.id),
      workTypeLabel,
    );

    const contractStart = contract.start_date?.trim() || null;
    const contractEnd = contract.end_date?.trim() || null;

    const contractMaxClosing = resolveContractMaxClosing(contractTasks);
    const contractClosingDisplay = contractMaxClosing
      ? formatDateShort(contractMaxClosing)
      : '—';
    const contractDelta = resolveContractDelta(contractEnd, contractMaxClosing, expenseReferenceDate);
    const contractBarOverdue = resolveBarOverdue(contractEnd, contractMaxClosing, expenseReferenceDate);
    const contractClosedOnTime =
      contractMaxClosing != null
        ? resolveExpenseClosedOnTime(contractEnd, contractMaxClosing)
        : null;

    const contractRow: ContractDeadlineTableRow = {
      key: `contract-${contract.id}`,
      contractId: contract.id,
      contractNumber: contract.contract_number,
      taskId: '',
      workType: '',
      startDate: contractStart,
      endDate: contractEnd,
      isTotal: false,
      isContractRow: true,
      isTaskRow: false,
      isCurrent: contract.id === currentContractId,
      showContractNumber: true,
      contractRowSpan: 1,
      showTaskId: false,
      taskRowSpan: 1,
      showWorkType: false,
      workTypeRowSpan: 1,
      expenseClosingDisplay: contractClosingDisplay,
      statusLabel: contractDelta.text,
      statusVariant: contractDelta.variant,
      deltaDisplay: contractDelta.text,
      deltaVariant: contractDelta.variant,
    };
    tableRows.push(contractRow);
    ganttRows.push({
      key: contractRow.key,
      startMs: parseTaskDay(contractStart),
      endMs: parseTaskDay(contractEnd),
      isCurrent: false,
      isTotal: false,
      tier: 'contract',
      barOverdue: contractBarOverdue,
      expenseClosingDateMs: parseTaskDay(contractMaxClosing),
      expenseClosingDateLabel: contractMaxClosing ? formatDateShort(contractMaxClosing) : null,
      expenseClosedOnTime: contractClosedOnTime,
    });

    const baseTaskRows = contractTasks.map((task) => {
      const startDate = task.start_date?.trim() || null;
      const endDate = task.end_date?.trim() || null;
      return {
        key: `task-${task.contracts_task_id}`,
        contractNumber: '',
        taskId: task.task_id,
        workType: workTypeLabel(task.work_type_id),
        startDate,
        endDate,
        isTotal: false,
        isContractRow: false,
        isTaskRow: true,
        isCurrent: false,
        showContractNumber: false,
        contractRowSpan: 1,
        showTaskId: false,
        taskRowSpan: 1,
        showWorkType: true,
        workTypeRowSpan: 1,
        expenseClosingDate: task.expense_closing_date ?? null,
      };
    });

    const taskSpans = mergeConsecutiveRowSpanCells(baseTaskRows, (row) => row.taskId);
    for (let index = 0; index < baseTaskRows.length; index += 1) {
      const row = baseTaskRows[index];
      const expenseClosingDateMs = parseTaskDay(row.expenseClosingDate);
      const expenseClosingDisplay = row.expenseClosingDate?.trim()
        ? formatDateShort(row.expenseClosingDate.trim())
        : '—';
      const closedOnTime = resolveExpenseClosedOnTime(row.endDate, row.expenseClosingDate);
      const delta = resolveTaskDelta(row.endDate, row.expenseClosingDate, expenseReferenceDate);
      const taskBarOverdue = resolveBarOverdue(
        row.endDate,
        row.expenseClosingDate?.trim() || null,
        expenseReferenceDate,
      );

      tableRows.push({
        ...row,
        showTaskId: taskSpans[index].show,
        taskRowSpan: taskSpans[index].rowSpan,
        expenseClosingDisplay,
        ...emptyRowStatus(),
        deltaDisplay: delta.text,
        deltaVariant: delta.variant,
      });
      ganttRows.push({
        key: row.key,
        startMs: parseTaskDay(row.startDate),
        endMs: parseTaskDay(row.endDate),
        isCurrent: false,
        isTotal: false,
        tier: 'task',
        expenseClosingDateMs,
        expenseClosingDateLabel: row.expenseClosingDate?.trim()
          ? formatDateShort(row.expenseClosingDate.trim())
          : null,
        expenseClosedOnTime: closedOnTime,
        barOverdue: taskBarOverdue,
      });
    }
  }

  const totalStart = minFamilyDate(familyContracts, 'start_date');
  const totalEnd = maxFamilyDate(familyContracts, 'end_date');

  const contractMaxClosings = familyContracts.map((contract) =>
    resolveContractMaxClosing(familyTasks.filter((t) => t.contract_id === contract.id)),
  );
  const totalMaxClosing = maxDate(contractMaxClosings);
  const totalClosingDisplay = totalMaxClosing ? formatDateShort(totalMaxClosing) : '—';
  const totalDelta = resolveContractDelta(totalEnd, totalMaxClosing, expenseReferenceDate);
  const totalBarOverdue = resolveBarOverdue(totalEnd, totalMaxClosing, expenseReferenceDate);
  const totalClosedOnTime =
    totalMaxClosing != null ? resolveExpenseClosedOnTime(totalEnd, totalMaxClosing) : null;

  tableRows.push({
    key: 'total',
    contractNumber: '',
    taskId: '',
    workType: '',
    startDate: totalStart,
    endDate: totalEnd,
    isTotal: true,
    isContractRow: false,
    isTaskRow: false,
    isCurrent: false,
    showContractNumber: true,
    contractRowSpan: 1,
    showTaskId: false,
    taskRowSpan: 1,
    showWorkType: false,
    workTypeRowSpan: 1,
    expenseClosingDisplay: totalClosingDisplay,
    statusLabel: totalDelta.text,
    statusVariant: totalDelta.variant,
    deltaDisplay: totalDelta.text,
    deltaVariant: totalDelta.variant,
  });

  ganttRows.push({
    key: 'total',
    startMs: parseTaskDay(totalStart),
    endMs: parseTaskDay(totalEnd),
    isCurrent: false,
    isTotal: true,
    totalMarkerMs,
    barOverdue: totalBarOverdue,
    expenseClosingDateMs: parseTaskDay(totalMaxClosing),
    expenseClosingDateLabel: totalMaxClosing ? formatDateShort(totalMaxClosing) : null,
    expenseClosedOnTime: totalClosedOnTime,
  });

  const spanStartMs = parseTaskDay(totalStart);
  const spanEndMs = parseTaskDay(totalEnd) ?? spanStartMs;
  let safeSpanStart = spanStartMs ?? todayMs;
  let safeSpanEnd = spanEndMs ?? safeSpanStart;

  const markedMs = collectMarkedDatesMs(ganttRows, totalMarkerMs);
  for (const ms of markedMs) {
    if (ms < safeSpanStart) safeSpanStart = ms;
    if (ms > safeSpanEnd) safeSpanEnd = ms;
  }
  if (!expenseSummary.allClosed) {
    if (todayMs < safeSpanStart) safeSpanStart = todayMs;
    if (todayMs > safeSpanEnd) safeSpanEnd = todayMs;
  }

  const spanContentEndMs = safeSpanEnd;
  safeSpanEnd = addCalendarMonths(safeSpanEnd, SPAN_END_PADDING_MONTHS);

  const axisMaxMs = markedMs.length > 0 ? Math.max(...markedMs) : spanContentEndMs;

  return {
    tableRows,
    ganttRows,
    spanStartMs: safeSpanStart,
    spanEndMs: safeSpanEnd,
    spanContentEndMs,
    axisStartLabel: totalStart ? formatDateShort(totalStart) : '—',
    axisEndLabel: markedMs.length > 0 ? formatMsAsDateShort(axisMaxMs) : (totalEnd ? formatDateShort(totalEnd) : '—'),
    totalOverdue: totalEnd ? totalBarOverdue : null,
    todayMs,
    referenceMarkerMs: totalMarkerMs,
    referenceMarkerLabel,
    familyExpenseClosingDisplay: totalClosingDisplay,
    summary: {
      contractCount: familyContracts.length,
      taskCount: uniqueTaskSummary.taskCount,
      overdueTaskCount: uniqueTaskSummary.overdueTaskCount,
      closedOnTimeCount: uniqueTaskSummary.closedOnTimeCount,
      allExpensesClosed: expenseSummary.allClosed,
    },
  };
}

export function buildContractStageDeadlinesData(params: {
  familyContracts: Contract[];
  familyStages: ContractStage[];
  currentContractId?: number;
}): ContractDeadlinesData {
  const { familyContracts, familyStages, currentContractId } = params;
  const empty: ContractDeadlinesData = {
    tableRows: [],
    ganttRows: [],
    spanStartMs: 0,
    spanEndMs: 0,
    spanContentEndMs: 0,
    axisStartLabel: '—',
    axisEndLabel: '—',
    totalOverdue: null,
    todayMs: todayStartMs(),
    referenceMarkerMs: todayStartMs(),
    referenceMarkerLabel: 'Сегодня',
    familyExpenseClosingDisplay: '—',
    summary: {
      contractCount: 0,
      taskCount: 0,
      overdueTaskCount: 0,
      closedOnTimeCount: 0,
      allExpensesClosed: false,
    },
  };

  if (familyContracts.length === 0) {
    return empty;
  }

  const todayMs = todayStartMs();
  const stageSummary = resolveFamilyStageClosingSummary(familyStages);
  const totalMarkerMs =
    stageSummary.allClosed && stageSummary.maxClosingMs != null ? stageSummary.maxClosingMs : todayMs;
  const expenseReferenceDate = msToDateString(totalMarkerMs);
  const referenceMarkerLabel =
    stageSummary.allClosed && stageSummary.maxClosingMs != null
      ? 'Закрытие этапов бухгалтерией'
      : 'Сегодня';

  const tableRows: ContractDeadlineTableRow[] = [];
  const ganttRows: TaskDeadlineGanttRow[] = [];
  const uniqueStageSummary = countStageSummary(familyStages, expenseReferenceDate);
  const stagesById = new Map(familyStages.map((stage) => [stage.id, stage]));
  const extensionsByParent = new Map<number, ContractStage[]>();
  for (const stage of familyStages) {
    const parentId = stage.extends_stage_id;
    const parent = parentId != null ? stagesById.get(parentId) : undefined;
    if (!parent || parent.id === stage.id) continue;
    const list = extensionsByParent.get(parent.id) ?? [];
    list.push(stage);
    extensionsByParent.set(parent.id, list);
  }
  for (const list of extensionsByParent.values()) {
    const sorted = sortContractStages(list);
    list.splice(0, list.length, ...sorted);
  }

  const pushStageRow = (
    stage: ContractStage,
    options: {
      isExtension: boolean;
      showStageLabel: boolean;
      stageRowSpan: number;
    },
  ): void => {
    const startDate = stage.planned_date_start?.trim() || null;
    const endDate = stage.planned_date_end?.trim() || null;
    const closingDate = stage.accounting_closing_date?.trim() || null;
    const expenseClosingDisplay = closingDate ? formatDateShort(closingDate) : '—';
    const closedOnTime = resolveExpenseClosedOnTime(endDate, closingDate);
    const delta = resolveTaskDelta(endDate, closingDate, expenseReferenceDate);
    const stageBarOverdue = resolveBarOverdue(endDate, closingDate, expenseReferenceDate);
    tableRows.push({
      key: `stage-${stage.id}`,
      contractNumber: '',
      taskId: options.isExtension ? '' : stageDeadlineLabel(stage),
      workType: options.isExtension ? `└ ${stageDeadlineLabel(stage)}` : '',
      startDate,
      endDate,
      isTotal: false,
      isContractRow: false,
      isTaskRow: true,
      isCurrent: false,
      showContractNumber: false,
      contractRowSpan: 1,
      showTaskId: options.showStageLabel,
      taskRowSpan: options.stageRowSpan,
      showWorkType: true,
      workTypeRowSpan: 1,
      expenseClosingDisplay,
      ...emptyRowStatus(),
      deltaDisplay: delta.text,
      deltaVariant: delta.variant,
    });
    ganttRows.push({
      key: `stage-${stage.id}`,
      startMs: parseTaskDay(startDate),
      endMs: parseTaskDay(endDate),
      isCurrent: false,
      isTotal: false,
      tier: 'task',
      expenseClosingDateMs: parseTaskDay(closingDate),
      expenseClosingDateLabel: closingDate ? formatDateShort(closingDate) : null,
      expenseClosedOnTime: closedOnTime,
      barOverdue: stageBarOverdue,
    });
  };

  for (const contract of familyContracts) {
    const contractStages = sortContractStages(
      familyStages.filter((stage) => stage.contract_id === contract.id),
    );
    const topLevel = contractStages.filter((stage) => {
      const parentId = stage.extends_stage_id;
      const parent = parentId != null ? stagesById.get(parentId) : undefined;
      return !parent || parent.id === stage.id;
    });

    const contractStart = contract.start_date?.trim() || null;
    const contractEnd = contract.end_date?.trim() || null;
    const contractMaxClosing = resolveContractMaxStageClosing(contractStages);
    const contractClosingDisplay = contractMaxClosing ? formatDateShort(contractMaxClosing) : '—';
    const contractDelta = resolveContractDelta(contractEnd, contractMaxClosing, expenseReferenceDate);
    const contractBarOverdue = resolveBarOverdue(contractEnd, contractMaxClosing, expenseReferenceDate);
    const contractClosedOnTime =
      contractMaxClosing != null ? resolveExpenseClosedOnTime(contractEnd, contractMaxClosing) : null;

    const contractRow: ContractDeadlineTableRow = {
      key: `contract-${contract.id}`,
      contractId: contract.id,
      contractNumber: contract.contract_number,
      taskId: '',
      workType: '',
      startDate: contractStart,
      endDate: contractEnd,
      isTotal: false,
      isContractRow: true,
      isTaskRow: false,
      isCurrent: contract.id === currentContractId,
      showContractNumber: true,
      contractRowSpan: 1,
      showTaskId: false,
      taskRowSpan: 1,
      showWorkType: false,
      workTypeRowSpan: 1,
      expenseClosingDisplay: contractClosingDisplay,
      statusLabel: contractDelta.text,
      statusVariant: contractDelta.variant,
      deltaDisplay: contractDelta.text,
      deltaVariant: contractDelta.variant,
    };
    tableRows.push(contractRow);
    ganttRows.push({
      key: contractRow.key,
      startMs: parseTaskDay(contractStart),
      endMs: parseTaskDay(contractEnd),
      isCurrent: false,
      isTotal: false,
      tier: 'contract',
      barOverdue: contractBarOverdue,
      expenseClosingDateMs: parseTaskDay(contractMaxClosing),
      expenseClosingDateLabel: contractMaxClosing ? formatDateShort(contractMaxClosing) : null,
      expenseClosedOnTime: contractClosedOnTime,
    });

    for (const stage of topLevel) {
      const extensions = extensionsByParent.get(stage.id) ?? [];
      pushStageRow(stage, {
        isExtension: false,
        showStageLabel: true,
        stageRowSpan: 1 + extensions.length,
      });
      for (const extension of extensions) {
        pushStageRow(extension, {
          isExtension: true,
          showStageLabel: false,
          stageRowSpan: 1,
        });
      }
    }
  }

  const totalStart = minFamilyDate(familyContracts, 'start_date');
  const totalEnd = maxFamilyDate(familyContracts, 'end_date');
  const contractMaxClosings = familyContracts.map((contract) =>
    resolveContractMaxStageClosing(familyStages.filter((stage) => stage.contract_id === contract.id)),
  );
  const totalMaxClosing = maxDate(contractMaxClosings);
  const totalClosingDate = stageSummary.allClosed ? totalMaxClosing : null;
  const totalClosingDisplay = totalClosingDate ? formatDateShort(totalClosingDate) : '—';
  const totalDelta = resolveContractDelta(totalEnd, totalClosingDate, expenseReferenceDate);
  const totalBarOverdue = resolveBarOverdue(totalEnd, totalClosingDate, expenseReferenceDate);
  const totalClosedOnTime =
    totalClosingDate != null ? resolveExpenseClosedOnTime(totalEnd, totalClosingDate) : null;

  tableRows.push({
    key: 'total',
    contractNumber: '',
    taskId: '',
    workType: '',
    startDate: totalStart,
    endDate: totalEnd,
    isTotal: true,
    isContractRow: false,
    isTaskRow: false,
    isCurrent: false,
    showContractNumber: true,
    contractRowSpan: 1,
    showTaskId: false,
    taskRowSpan: 1,
    showWorkType: false,
    workTypeRowSpan: 1,
    expenseClosingDisplay: totalClosingDisplay,
    statusLabel: totalDelta.text,
    statusVariant: totalDelta.variant,
    deltaDisplay: totalDelta.text,
    deltaVariant: totalDelta.variant,
  });

  ganttRows.push({
    key: 'total',
    startMs: parseTaskDay(totalStart),
    endMs: parseTaskDay(totalEnd),
    isCurrent: false,
    isTotal: true,
    totalMarkerMs,
    barOverdue: totalBarOverdue,
    expenseClosingDateMs: parseTaskDay(totalClosingDate),
    expenseClosingDateLabel: totalClosingDate ? formatDateShort(totalClosingDate) : null,
    expenseClosedOnTime: totalClosedOnTime,
  });

  const spanStartMs = parseTaskDay(totalStart);
  const spanEndMs = parseTaskDay(totalEnd) ?? spanStartMs;
  let safeSpanStart = spanStartMs ?? todayMs;
  let safeSpanEnd = spanEndMs ?? safeSpanStart;

  const markedMs = collectMarkedDatesMs(ganttRows, totalMarkerMs);
  for (const ms of markedMs) {
    if (ms < safeSpanStart) safeSpanStart = ms;
    if (ms > safeSpanEnd) safeSpanEnd = ms;
  }
  if (!stageSummary.allClosed) {
    if (todayMs < safeSpanStart) safeSpanStart = todayMs;
    if (todayMs > safeSpanEnd) safeSpanEnd = todayMs;
  }

  const spanContentEndMs = safeSpanEnd;
  safeSpanEnd = addCalendarMonths(safeSpanEnd, SPAN_END_PADDING_MONTHS);
  const axisMaxMs = markedMs.length > 0 ? Math.max(...markedMs) : spanContentEndMs;

  return {
    tableRows,
    ganttRows,
    spanStartMs: safeSpanStart,
    spanEndMs: safeSpanEnd,
    spanContentEndMs,
    axisStartLabel: totalStart ? formatDateShort(totalStart) : '—',
    axisEndLabel: markedMs.length > 0 ? formatMsAsDateShort(axisMaxMs) : (totalEnd ? formatDateShort(totalEnd) : '—'),
    totalOverdue: totalEnd ? totalBarOverdue : null,
    todayMs,
    referenceMarkerMs: totalMarkerMs,
    referenceMarkerLabel,
    familyExpenseClosingDisplay: totalClosingDisplay,
    summary: {
      contractCount: familyContracts.length,
      taskCount: uniqueStageSummary.taskCount,
      overdueTaskCount: uniqueStageSummary.overdueTaskCount,
      closedOnTimeCount: uniqueStageSummary.closedOnTimeCount,
      allExpensesClosed: stageSummary.allClosed,
    },
  };
}

export interface TaskIdDeadlineTableRow {
  key: string;
  taskId: string;
  stageLabel: string;
  startDate: string | null;
  endDate: string | null;
  isTotal: boolean;
  isTaskIdRow: boolean;
  isStageRow: boolean;
  expenseClosingDisplay: string;
  deltaDisplay: string;
  deltaVariant: DeadlineStatusVariant;
}

export type TaskIdDeadlinesData = Omit<ContractDeadlinesData, 'tableRows'> & {
  tableRows: TaskIdDeadlineTableRow[];
};

function minDate(values: Array<string | null | undefined>): string | null {
  let min: string | null = null;
  for (const raw of values) {
    const v = raw?.trim();
    if (!v) continue;
    if (min == null || v < min) min = v;
  }
  return min;
}

function countContractsInTasks(tasks: Task[]): number {
  return new Set(tasks.map((task) => task.contract_id)).size;
}

export function buildTaskIdDeadlinesData(params: {
  tasks: Task[];
  taskId: string;
  stages: ContractStage[];
}): TaskIdDeadlinesData {
  const { tasks, taskId, stages } = params;
  const empty: TaskIdDeadlinesData = {
    tableRows: [],
    ganttRows: [],
    spanStartMs: 0,
    spanEndMs: 0,
    spanContentEndMs: 0,
    axisStartLabel: '—',
    axisEndLabel: '—',
    totalOverdue: null,
    todayMs: todayStartMs(),
    referenceMarkerMs: todayStartMs(),
    referenceMarkerLabel: 'Сегодня',
    familyExpenseClosingDisplay: '—',
    summary: {
      contractCount: 0,
      taskCount: 0,
      overdueTaskCount: 0,
      closedOnTimeCount: 0,
      allExpensesClosed: false,
    },
  };

  if (tasks.length === 0) {
    return empty;
  }

  const todayMs = todayStartMs();
  const stageSummary = resolveFamilyStageClosingSummary(stages);
  const totalMarkerMs =
    stageSummary.allClosed && stageSummary.maxClosingMs != null ? stageSummary.maxClosingMs : todayMs;
  const expenseReferenceDate = msToDateString(totalMarkerMs);
  const referenceMarkerLabel =
    stageSummary.allClosed && stageSummary.maxClosingMs != null
      ? 'Закрытие этапов бухгалтерией'
      : 'Сегодня';
  const uniqueStageSummary = countStageSummary(stages, expenseReferenceDate);

  const sortedStages = sortContractStages(stages);
  const stagesById = new Map(sortedStages.map((stage) => [stage.id, stage]));
  const extensionsByParent = new Map<number, ContractStage[]>();
  for (const stage of sortedStages) {
    const parentId = stage.extends_stage_id;
    const parent = parentId != null ? stagesById.get(parentId) : undefined;
    if (!parent || parent.id === stage.id) continue;
    const list = extensionsByParent.get(parent.id) ?? [];
    list.push(stage);
    extensionsByParent.set(parent.id, list);
  }
  for (const list of extensionsByParent.values()) {
    const nested = sortContractStages(list);
    list.splice(0, list.length, ...nested);
  }

  const nestedKeys = new Set<number>();
  for (const list of extensionsByParent.values()) {
    for (const stage of list) nestedKeys.add(stage.id);
  }
  const topLevel = sortedStages.filter((stage) => !nestedKeys.has(stage.id));

  const stageRows: Array<TaskIdDeadlineTableRow & { gantt: TaskDeadlineGanttRow }> = [];
  const pushStageRow = (stage: ContractStage, isExtension: boolean): void => {
    const startDate = stage.planned_date_start?.trim() || null;
    const endDate = stage.planned_date_end?.trim() || null;
    const closingDate = stage.accounting_closing_date?.trim() || null;
    const expenseClosingDisplay = closingDate ? formatDateShort(closingDate) : '—';
    const delta = resolveTaskDelta(endDate, closingDate, expenseReferenceDate);
    const stageBarOverdue = resolveBarOverdue(endDate, closingDate, expenseReferenceDate);
    const label = stageDeadlineLabel(stage);
    stageRows.push({
      key: `stage-${stage.id}`,
      taskId: '',
      stageLabel: isExtension ? `└ ${label}` : label,
      startDate,
      endDate,
      isTotal: false,
      isTaskIdRow: false,
      isStageRow: true,
      expenseClosingDisplay,
      deltaDisplay: delta.text,
      deltaVariant: delta.variant,
      gantt: {
        key: `stage-${stage.id}`,
        startMs: parseTaskDay(startDate),
        endMs: parseTaskDay(endDate),
        isCurrent: false,
        isTotal: false,
        tier: 'task',
        expenseClosingDateMs: parseTaskDay(closingDate),
        expenseClosingDateLabel: closingDate ? formatDateShort(closingDate) : null,
        expenseClosedOnTime: resolveExpenseClosedOnTime(endDate, closingDate),
        barOverdue: stageBarOverdue,
      },
    });
  };

  for (const stage of topLevel) {
    pushStageRow(stage, false);
    for (const extension of extensionsByParent.get(stage.id) ?? []) {
      pushStageRow(extension, true);
    }
  }

  const taskStart =
    minDate(sortedStages.map((stage) => stage.planned_date_start))
    ?? minDate(tasks.map((task) => task.start_date));
  const taskEnd =
    maxDate(sortedStages.map((stage) => stage.planned_date_end))
    ?? maxDate(tasks.map((task) => task.end_date));
  const taskMaxClosing = resolveContractMaxStageClosing(sortedStages);
  const taskClosingDisplay = taskMaxClosing ? formatDateShort(taskMaxClosing) : '—';
  const taskDelta = resolveContractDelta(taskEnd, taskMaxClosing, expenseReferenceDate);
  const taskBarOverdue = resolveBarOverdue(taskEnd, taskMaxClosing, expenseReferenceDate);
  const taskClosedOnTime =
    taskMaxClosing != null ? resolveExpenseClosedOnTime(taskEnd, taskMaxClosing) : null;

  const taskIdRow: TaskIdDeadlineTableRow = {
    key: `task-id-${taskId}`,
    taskId,
    stageLabel: '',
    startDate: taskStart,
    endDate: taskEnd,
    isTotal: false,
    isTaskIdRow: true,
    isStageRow: false,
    expenseClosingDisplay: taskClosingDisplay,
    deltaDisplay: taskDelta.text,
    deltaVariant: taskDelta.variant,
  };

  const totalRow: TaskIdDeadlineTableRow = {
    key: 'total',
    taskId: '',
    stageLabel: '',
    startDate: taskStart,
    endDate: taskEnd,
    isTotal: true,
    isTaskIdRow: false,
    isStageRow: false,
    expenseClosingDisplay: taskClosingDisplay,
    deltaDisplay: taskDelta.text,
    deltaVariant: taskDelta.variant,
  };

  const tableRows: TaskIdDeadlineTableRow[] = [taskIdRow, ...stageRows, totalRow];
  const ganttRows: TaskDeadlineGanttRow[] = [
    {
      key: taskIdRow.key,
      startMs: parseTaskDay(taskStart),
      endMs: parseTaskDay(taskEnd),
      isCurrent: false,
      isTotal: false,
      tier: 'contract',
      barOverdue: taskBarOverdue,
      expenseClosingDateMs: parseTaskDay(taskMaxClosing),
      expenseClosingDateLabel: taskMaxClosing ? formatDateShort(taskMaxClosing) : null,
      expenseClosedOnTime: taskClosedOnTime,
    },
    ...stageRows.map((row) => row.gantt),
    {
      key: 'total',
      startMs: parseTaskDay(taskStart),
      endMs: parseTaskDay(taskEnd),
      isCurrent: false,
      isTotal: true,
      totalMarkerMs,
      barOverdue: taskBarOverdue,
      expenseClosingDateMs: parseTaskDay(taskMaxClosing),
      expenseClosingDateLabel: taskMaxClosing ? formatDateShort(taskMaxClosing) : null,
      expenseClosedOnTime: taskClosedOnTime,
    },
  ];

  const spanStartMs = parseTaskDay(taskStart);
  const spanEndMs = parseTaskDay(taskEnd) ?? spanStartMs;
  let safeSpanStart = spanStartMs ?? todayMs;
  let safeSpanEnd = spanEndMs ?? safeSpanStart;

  const markedMs = collectMarkedDatesMs(ganttRows, totalMarkerMs);
  for (const ms of markedMs) {
    if (ms < safeSpanStart) safeSpanStart = ms;
    if (ms > safeSpanEnd) safeSpanEnd = ms;
  }
  if (!stageSummary.allClosed) {
    if (todayMs < safeSpanStart) safeSpanStart = todayMs;
    if (todayMs > safeSpanEnd) safeSpanEnd = todayMs;
  }

  const spanContentEndMs = safeSpanEnd;
  safeSpanEnd = addCalendarMonths(safeSpanEnd, SPAN_END_PADDING_MONTHS);
  const axisMaxMs = markedMs.length > 0 ? Math.max(...markedMs) : spanContentEndMs;

  return {
    tableRows,
    ganttRows,
    spanStartMs: safeSpanStart,
    spanEndMs: safeSpanEnd,
    spanContentEndMs,
    axisStartLabel: taskStart ? formatDateShort(taskStart) : '—',
    axisEndLabel: markedMs.length > 0 ? formatMsAsDateShort(axisMaxMs) : (taskEnd ? formatDateShort(taskEnd) : '—'),
    totalOverdue: taskEnd ? taskBarOverdue : null,
    todayMs,
    referenceMarkerMs: totalMarkerMs,
    referenceMarkerLabel,
    familyExpenseClosingDisplay: taskClosingDisplay,
    summary: {
      contractCount: countContractsInTasks(tasks),
      taskCount: uniqueStageSummary.taskCount,
      overdueTaskCount: uniqueStageSummary.overdueTaskCount,
      closedOnTimeCount: uniqueStageSummary.closedOnTimeCount,
      allExpensesClosed: stageSummary.allClosed,
    },
  };
}
